import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Country } from "@/types/country";
import { toast } from "sonner";

export const useCountries = () => {
  return useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("countries")
        .select("*")
        .order("name_ar");

      if (error) {
        console.error("Error fetching countries:", error);
        throw error;
      }

      return data as Country[] || [];
    },
  });
};

export const useCreateCountry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (countryData: Omit<Country, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from("countries")
        .insert([countryData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("تم إنشاء الدولة بنجاح");
    },
    onError: (error) => {
      console.error("Error creating country:", error);
      toast.error("حدث خطأ أثناء إنشاء الدولة");
    },
  });
};

export const useUpdateCountry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...countryData }: Partial<Country> & { id: string }) => {
      const { data, error } = await supabase
        .from("countries")
        .update(countryData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("تم تحديث الدولة بنجاح");
    },
    onError: (error) => {
      console.error("Error updating country:", error);
      toast.error("حدث خطأ أثناء تحديث الدولة");
    },
  });
};

export const useDeleteCountry = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (countryId: string) => {
      const { error } = await supabase
        .from("countries")
        .delete()
        .eq("id", countryId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("تم حذف الدولة بنجاح");
    },
    onError: (error) => {
      console.error("Error deleting country:", error);
      toast.error("حدث خطأ أثناء حذف الدولة");
    },
  });
};